import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Wallet } from '@asia/shared';
import { haptic } from '../../hooks/useTelegram';
import { useBackHandler } from '../../hooks/useBackHandler';
import { useNav } from '../../store/nav';
import { useWallets } from '../../store/wallets';
import { useToast } from '../../store/toast';
import { copyText } from '../../lib/clipboard';
import { CoinIcon } from '../CoinIcon';
import { FigIcon } from '../FigIcon';
import { WalletDetailSheet } from './WalletDetailSheet';
import { DeleteWalletDialog } from './DeleteWalletDialog';
import { RenameWalletDialog } from './RenameWalletDialog';

interface Props {
  wallets: Wallet[];
  activeId: string;
  onSelect: (id: string) => void;
}

/** Up/down swap chevron (⇕) matching the Figma header. */
function SwapChevron({ className }: { className?: string }) {
  return (
    <svg width={12} height={16} viewBox="0 0 12 16" fill="none" className={className}>
      <path
        d="M6 2 3 5M6 2l3 3M6 14l-3-3M6 14l3-3"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Wallet-name selector shown above the balance card — Tron logo + wallet name +
 *  up/down chevron, matching the Figma header. Tap opens a wallet-list popover. */
export function WalletSwitcher({ wallets, activeId, onSelect }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<Wallet | null>(null);
  const [deleting, setDeleting] = useState<Wallet | null>(null);
  const [renaming, setRenaming] = useState<Wallet | null>(null);
  const [creating, setCreating] = useState(false);
  const push = useNav((s) => s.push);
  const addWallet = useWallets((s) => s.add);
  const renameWallet = useWallets((s) => s.rename);
  const removeWallet = useWallets((s) => s.remove);
  const showToast = useToast((s) => s.show);
  const active = wallets.find((w) => w.id === activeId) ?? wallets[0]!;
  // The native Telegram BackButton closes the open wallet-list popover.
  useBackHandler(open, () => setOpen(false));

  // Add a wallet in place — derive the next address, register it, and switch to
  // it — instead of opening a separate create page.
  const createWallet = async () => {
    if (creating) return;
    haptic('medium');
    setCreating(true);
    try {
      const w = await addWallet();
      onSelect(w.id);
      showToast(t('wallet.created'));
    } catch {
      showToast(t('wallet.createFailed'));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="relative z-30 flex justify-center">
      <button
        onClick={() => {
          haptic();
          setOpen((o) => !o);
        }}
        className="flex items-center gap-2 px-2 py-1 active:opacity-70"
      >
        <CoinIcon symbol="TRX" size={22} />
        <span className="max-w-[190px] truncate text-[15px] font-semibold text-ink">
          {active.name}
        </span>
        <SwapChevron className="text-muted" />
      </button>

      {open && (
        <>
          {/* tap anywhere outside the popover closes it */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden />
          {/* expanded popover — matches Figma node 3:5156 exactly */}
          <div
            className="fade-in absolute top-[calc(100%+8px)] z-50 flex w-[320px] flex-col gap-4 rounded-[28px] p-4 backdrop-blur-[2px]"
            style={{
              backgroundColor: 'rgb(var(--sheet) / 0.8)',
              boxShadow: '0px 0px 40px 0px rgba(0,0,0,0.12)',
            }}
          >
            {/* wallet list scrolls when it's taller than the popover so the long
                list stays reachable; the divider + create button stay pinned. */}
            <div className="no-scrollbar -mr-2 flex max-h-[44vh] flex-col gap-4 overflow-y-auto pr-2">
            {wallets.map((w) => (
              <div key={w.id} className="flex h-9 w-full shrink-0 items-center justify-between">
                <button
                  onClick={() => {
                    haptic();
                    onSelect(w.id);
                    setOpen(false);
                  }}
                  className="flex min-w-0 flex-1 flex-col items-start gap-[5px] text-left"
                >
                  <span className="truncate text-[15px] font-medium text-ink">{w.name}</span>
                  <span className="flex items-center gap-[5px] text-[13px] text-muted">
                    {w.address.slice(0, 11)}…
                    <span
                      role="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        void copyText(w.address);
                      }}
                    >
                      <FigIcon name="copy" size={16} className="text-muted" />
                    </span>
                  </span>
                </button>
                <button
                  aria-label={t('wallet.settings')}
                  onClick={() => {
                    haptic();
                    setDetail(w);
                    setOpen(false);
                  }}
                  className="ml-2 grid size-8 shrink-0 place-items-center rounded-full bg-press/[0.05] active:opacity-70"
                >
                  <FigIcon name="pfSetting" size={18} className="text-muted" />
                </button>
              </div>
            ))}
            </div>

            <div className="h-px w-full bg-hairline" />

            <button
              onClick={createWallet}
              disabled={creating}
              className="flex items-center justify-center gap-[6px] active:opacity-70 disabled:opacity-60"
            >
              {creating ? (
                <span className="size-5 animate-spin rounded-full border-2 border-border border-t-black" />
              ) : (
                <FigIcon name="add" size={20} className="text-ink" />
              )}
              <span className="text-[14px] text-ink">{t('wallet.createWallet')}</span>
            </button>
          </div>
        </>
      )}

      {detail && (
        <WalletDetailSheet
          wallet={detail}
          onClose={() => setDetail(null)}
          onSeed={() => {
            setDetail(null);
            push({ name: 'seed', walletId: detail.id });
          }}
          onRename={() => {
            setRenaming(detail);
            setDetail(null);
          }}
          onDelete={() => {
            setDeleting(detail);
            setDetail(null);
          }}
        />
      )}

      {renaming && (
        <RenameWalletDialog
          wallet={renaming}
          onConfirm={(name) => {
            renameWallet(renaming.id, name);
            setRenaming(null);
          }}
          onCancel={() => setRenaming(null)}
        />
      )}

      {deleting && (
        <DeleteWalletDialog
          wallet={deleting}
          onConfirm={() => {
            removeWallet(deleting.id);
            setDeleting(null);
          }}
          onCancel={() => setDeleting(null)}
        />
      )}
    </div>
  );
}
